import type { CountryCode } from './types';

/**
 * Minimal flag SVGs. Windows Chrome refuses to render regional-indicator
 * emoji as flags (falls back to the country code letters), so we ship
 * inline SVG instead. Simplified geometry — stripes only, no sun/eagle —
 * which is enough at the sizes we use (sidebar / dropdown).
 */

type Props = {
  code: CountryCode;
  className?: string;
  /** Pixel size; defaults to 14 (matches text-xs line-height). */
  size?: number;
  title?: string;
};

export function FlagIcon({ code, className, size = 14, title }: Props) {
  const w = Math.round(size * 1.5); // 3:2 aspect, matches both AR and MX
  const h = size;
  return (
    <svg
      role="img"
      aria-label={title}
      width={w}
      height={h}
      viewBox="0 0 30 20"
      className={className}
      style={{ display: 'inline-block', flexShrink: 0 }}
    >
      <title>{title ?? code}</title>
      <rect width="30" height="20" rx="2" ry="2" fill="white" />
      <clipPath id={`flag-clip-${code}`}>
        <rect width="30" height="20" rx="2" ry="2" />
      </clipPath>
      <g clipPath={`url(#flag-clip-${code})`}>
        {code === 'AR' ? (
          <>
            {/* Light blue, white, light blue */}
            <rect y="0" width="30" height="6.67" fill="#74ACDF" />
            <rect y="6.67" width="30" height="6.66" fill="#FFFFFF" />
            <rect y="13.33" width="30" height="6.67" fill="#74ACDF" />
            {/* Stylized sun: a small yellow disc in the centre. */}
            <circle cx="15" cy="10" r="2" fill="#F6B40E" />
          </>
        ) : (
          <>
            {/* Green, white, red */}
            <rect x="0" width="10" height="20" fill="#006847" />
            <rect x="10" width="10" height="20" fill="#FFFFFF" />
            <rect x="20" width="10" height="20" fill="#CE1126" />
            {/* Stylized centre seal: a small dark mark in the white field. */}
            <circle cx="15" cy="10" r="1.6" fill="#7A4F1D" />
          </>
        )}
      </g>
      {/* Hairline border so the white field has an edge on light backgrounds. */}
      <rect
        x="0.5"
        y="0.5"
        width="29"
        height="19"
        rx="1.5"
        ry="1.5"
        fill="none"
        stroke="rgba(0,0,0,0.12)"
      />
    </svg>
  );
}
